import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ageEnAnnees, type CategorieDossier } from '@repere/core';
import { useEffect, useRef, useState } from 'react';
import { maintenant } from '../horloge';
import { Alerte, Bouton, Carte, Champ, Invisible } from '../ui';
import { ECRAN_LARGE, useRequeteMedia } from '../ui/media';
import { messageDErreur, useSession } from '../auth/session';
import { ChoisirFormule } from '../abonnement/ChoisirFormule';
import { PresentationFormules } from '../abonnement/PresentationFormules';
import { useTraduction } from '../i18n/langue';
import { AjouterEnfant } from '../dossier/AjouterEnfant';
import { Calendrier } from '../dossier/Calendrier';
import { DemandeEchange } from '../dossier/DemandeEchange';
import { Depenses } from '../dossier/Depenses';
import { FicheEnfant } from '../dossier/FicheEnfant';
import { HistoriqueSchema } from '../dossier/HistoriqueSchema';
import { Journal } from '../dossier/Journal';
import { Messagerie } from '../dossier/Messagerie';
import { AlerteRecue } from '../dossier/AlerteRecue';
import { Navigation } from '../dossier/Navigation';
import { Nouveautes, type Changements } from '../dossier/Nouveautes';
import { NomDuDossier } from '../dossier/NomDuDossier';
import { Propositions, usePropositions } from '../dossier/Propositions';
import { QuiALEnfant } from '../dossier/QuiALEnfant';
import { SelecteurEnfant } from '../dossier/SelecteurEnfant';
import { BandeauSuppression, SupprimerDossier } from '../dossier/SupprimerDossier';
import type { Dossier as DossierType } from '../dossier/types';

/**
 * Les demandes d'accord — intitulé compris — ne paraissent que s'il y en a.
 *
 * Le volet portait en permanence son titre et, dessous, un bloc « Aucune demande d'accord
 * pour l'instant ». Une demande d'accord est un **événement**, pas un état : la liste est
 * vide la plupart du temps, et l'écran qu'on ouvre pour savoir qui a l'enfant ce soir
 * consacrait un tiers de sa hauteur à annoncer l'absence de quelque chose. Ce que le
 * produit doit dire ici, il le dit déjà quand il y a une demande.
 *
 * C'est une exception assumée à la règle du bloc vide (`ui/Vide.tsx`), qui vaut pour les
 * catégories qu'on ouvre exprès — « aucune dépense pour l'instant » répond à quelqu'un qui
 * est venu voir les dépenses. Personne n'ouvre « Aujourd'hui » pour voir ses demandes
 * d'accord : elles s'y trouvent parce qu'il faut y répondre quand il y en a.
 *
 * **L'erreur garde son intitulé.** Une liste qui n'a pas pu être chargée n'est pas une
 * liste vide ; la faire disparaître en silence reviendrait à affirmer qu'il n'y a rien.
 *
 * Le volet est un composant, et non une condition écrite dans `Dossier` : c'est lui qui
 * appelle `usePropositions`, donc la requête ne part que là où la liste se rendait déjà.
 * Le même appel posé dans `Dossier` aurait interrogé `/proposals` depuis le calendrier,
 * les dépenses et la messagerie, où rien ne l'affiche.
 */
function VoletDemandes({
  caseFileId,
  monUserId,
  titre,
  enCarte,
}: {
  caseFileId: string;
  monUserId: string | null;
  titre: string;
  enCarte: boolean;
}) {
  const demandes = usePropositions(caseFileId);

  // Pendant le chargement, rien : le volet paraîtra s'il a quelque chose à montrer. Un
  // titre posé d'avance sur un contenu qui peut ne jamais venir est la moitié du défaut
  // qu'on corrige ici.
  if (!demandes.isError && (demandes.data?.length ?? 0) === 0) return null;

  const liste = <Propositions caseFileId={caseFileId} monUserId={monUserId} />;

  // Sur téléphone, un simple intitulé : la carte est le volet entier (`.volet-carte`), et
  // une carte dans une carte ne délimiterait rien. L'intitulé, lui, reste nécessaire —
  // rien d'autre ne nomme cette liste dans le volet.
  return enCarte ? (
    <Carte titre={titre}>{liste}</Carte>
  ) : (
    <>
      <h3>{titre}</h3>
      {liste}
    </>
  );
}

/**
 * Vue d'un dossier, organisée en catégories (T-044).
 *
 * La première version empilait dix cartes sur une seule page. Ce n'était pas seulement
 * long : un parent qui ouvre Repère cherche une chose précise — chez qui dort l'enfant ce
 * soir, répondre à un message, retrouver une facture — et le faire descendre au milieu de
 * tout le reste lui coûte quelque chose à chaque visite, tous les jours, pendant des
 * années.
 *
 * Trois principes tiennent l'écran :
 *
 * - **La catégorie est dans l'URL.** Un lien vers la messagerie d'un dossier s'envoie et
 *   s'ouvre, et le bouton « précédent » du navigateur revient d'où l'on vient. Un onglet
 *   qui ne vit qu'en mémoire casse les deux.
 * - **La visite d'une catégorie efface sa pastille**, pas celles des autres : une pastille
 *   qui disparaîtrait parce qu'on est entré par une autre porte perdrait ce qu'elle disait.
 * - **Le mode parent seul reste nommé** comme un état de fonctionnement normal, jamais
 *   comme un manque ni comme une faute de l'autre parent (docs/02 §1.4).
 */
export function Dossier({
  dossierId,
  categorie,
  surCategorie,
  enfantId,
  surEnfant,
}: {
  dossierId: string;
  categorie: CategorieDossier;
  surCategorie: (categorie: CategorieDossier) => void;
  /** Enfant affiché, ou `null` pour tous. Vient de l'URL. */
  enfantId?: string | null;
  surEnfant?: (enfantId: string | null) => void;
}) {
  const { client, profil } = useSession();
  const { messages } = useTraduction();
  const m = messages.dossier.ecran;
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  // Le formulaire d'invitation rouvert alors qu'une invitation court déjà : adresse mal
  // saisie, message perdu. Sans cette porte, l'écran n'offrait plus aucune action tant que
  // l'invitation n'avait pas expiré.
  const [renvoi, setRenvoi] = useState(false);

  /**
   * « Aujourd'hui » en trois volets, **sur téléphone seulement**.
   *
   * L'écran empilait quatre blocs qu'il fallait dérouler : le relevé des nouveautés, la
   * garde du jour, les demandes d'accord, et l'état du dossier — auxquels s'ajoutait un
   * en-tête portant le mode, les âges et le régime. Sur 360 px, la garde du jour, qui est
   * la question qu'on vient poser, arrivait sous deux écrans de défilement.
   *
   * Les volets ne cachent rien qu'on ne retrouve : ils remplacent un défilement par un
   * geste, et chacun porte sa pastille quand il a quelque chose à annoncer.
   *
   * **Le rail de l'écran large n'y touche pas.** Là où la hauteur existe, l'empilement se
   * lit d'un regard, et le découper obligerait à trois clics pour voir ce qui tenait déjà
   * sur une page.
   */
  const rail = useRequeteMedia(ECRAN_LARGE);
  const [volet, setVolet] = useState<'garde' | 'nouveautes' | 'dossier'>('garde');

  /**
   * « Calendrier » en trois volets, **sur téléphone seulement** — même dessin qu'« Aujourd'hui »
   * ci-dessus, pour la même raison : la grille du mois, la demande d'échange et l'historique
   * du schéma s'empilaient en trois cartes pleine largeur, et la grille — ce qu'on vient
   * consulter — arrivait après un défilement. Le rail garde l'empilement : rien n'y est à
   * l'étroit.
   */
  const [voletCalendrier, setVoletCalendrier] = useState<'grille' | 'echange' | 'historique'>(
    'grille',
  );

  const dossier = useQuery({
    queryKey: ['case-file', dossierId],
    queryFn: () => client.request<DossierType>(`/case-files/${dossierId}`),
  });

  const changements = useQuery({
    queryKey: ['changes', dossierId],
    queryFn: () => client.request<Changements>(`/case-files/${dossierId}/changes`),
  });

  /**
   * Le volet « Aujourd'hui » s'ouvre sur les nouveautés — toutes catégories confondues,
   * pas seulement celles qui touchent « Aujourd'hui » — plutôt que sur « Qui a l'enfant ? »,
   * quand il y a quelque chose à lire. La pastille du volet l'annonçait déjà ; encore
   * fallait-il l'ouvrir pour le voir.
   *
   * **Une seule fois**, à l'arrivée sur le dossier : un message qui arrive pendant que le
   * parent regarde « Qui a l'enfant ? » ne doit pas l'en faire déloger, et un onglet déjà
   * choisi à la main ne doit jamais se rouvrir tout seul.
   */
  const voletInitialApplique = useRef(false);
  useEffect(() => {
    if (voletInitialApplique.current || changements.data === undefined) return;
    voletInitialApplique.current = true;
    const total = Object.values(changements.data.parCategorie).reduce((somme, n) => somme + n, 0);
    if (total > 0) setVolet('nouveautes');
  }, [changements.data]);

  // Un onglet choisi à la main compte comme l'arrivée déjà traitée : l'effet ci-dessus ne
  // doit plus rouvrir « Nouveautés » de force si `changements` finit de charger après coup.
  const choisirVolet = (choix: 'garde' | 'nouveautes' | 'dossier') => {
    voletInitialApplique.current = true;
    setVolet(choix);
  };

  // La catégorie ouverte, lue par le flux sans le rouvrir à chaque changement d'onglet.
  const categorieOuverte = useRef(categorie);
  categorieOuverte.current = categorie;

  /**
   * Les pastilles se mettent à jour en direct, sur toutes les catégories.
   *
   * L'abonnement vivait dans la messagerie, donc uniquement quand l'onglet Messages était
   * affiché : une dépense ajoutée par l'autre parent n'apparaissait qu'au rechargement de
   * la page, et un message reçu depuis un autre onglet non plus. Il vit maintenant avec le
   * dossier, et un seul suffit pour tout l'écran.
   *
   * La catégorie ouverte est **marquée vue avant** la relecture : sans cela, un fait qui
   * arrive pendant qu'on le regarde s'annoncerait comme nouveau sur l'onglet où l'on se
   * trouve déjà. L'échec est silencieux — au pire une pastille reparaît, ce qui vaut mieux
   * qu'une erreur pour un repère.
   */
  useEffect(() => {
    const releverLaCategorieOuverte = (): void => {
      void client
        .request(`/case-files/${dossierId}/visits`, {
          method: 'POST',
          body: { category: categorieOuverte.current },
        })
        .catch(() => undefined)
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: ['changes', dossierId] });
          void queryClient.invalidateQueries({ queryKey: ['messages', dossierId] });
          // L'alerte urgente est la seule qui doit s'imposer sans que rien n'ait été
          // demandé : elle ne peut pas attendre le prochain balayage de trente secondes.
          void queryClient.invalidateQueries({ queryKey: ['alertes', dossierId] });
        });
    };

    const flux = client.openMessageStream(dossierId, releverLaCategorieOuverte, {
      // Une reprise du flux ouvre sur un trou : le téléphone était en arrière-plan, hors
      // réseau, ou le proxy avait fermé une connexion muette. Ce qui est arrivé pendant ce
      // temps n'a produit aucun événement — il faut le redemander, sans quoi les pastilles
      // ne repartiraient qu'au message **suivant**.
      onReconnect: releverLaCategorieOuverte,
    });
    return () => flux.close();
  }, [client, queryClient, dossierId]);

  // La visite est enregistrée **après** que la catégorie a été affichée, jamais avant :
  // marquer vu ce qui n'est pas encore à l'écran ferait disparaître des nouveautés que
  // personne n'a lues. L'échec est silencieux — un repère manquant fait au pire reparaître
  // une pastille, ce qui vaut mieux qu'une erreur pour un signet.
  useEffect(() => {
    let annule = false;
    void client
      .request(`/case-files/${dossierId}/visits`, { method: 'POST', body: { category: categorie } })
      .then(() => {
        if (!annule) void queryClient.invalidateQueries({ queryKey: ['changes', dossierId] });
      })
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, [client, queryClient, dossierId, categorie]);

  const inviter = useMutation({
    mutationFn: () =>
      client.request(`/case-files/${dossierId}/invitations`, {
        method: 'POST',
        body: { email },
      }),
    onSuccess: () => {
      setEmail('');
      setRenvoi(false);
      void queryClient.invalidateQueries({ queryKey: ['case-file', dossierId] });
    },
  });

  if (dossier.isPending) return <p className="discret">Chargement du dossier…</p>;

  if (dossier.isError) {
    return (
      <Alerte titre="Ce dossier n’a pas pu être ouvert">{messageDErreur(dossier.error)}</Alerte>
    );
  }

  const { state, parents } = dossier.data;
  const monUserId = profil?.id ?? null;
  // Le prénom de l'autre parent, pour le badge de l'en-tête. L'accueil le nomme sur chaque
  // carte ; le dossier lui-même l'avait perdu, et « Dossier partagé » tout court ne dit pas
  // ce qu'on cherche du regard en arrivant — avec qui.
  const autreParent = parents.find((parent) => !parent.isViewer);
  const enfants = dossier.data.children;
  const ages = enfants
    .map(
      (enfant) =>
        `${enfant.firstName}, ${ageEnAnnees(new Date(enfant.birthDate), maintenant())} ans`,
    )
    .join(' · ');

  // Les pastilles ne s'affichent pas tant que les nouveautés n'ont pas été relevées :
  // en faire clignoter puis disparaître donnerait l'impression d'avoir raté quelque chose.
  const parCategorie = changements.data?.parCategorie ?? {};

  // Sans entrée « Tous », il faut toujours un enfant affiché : celui de l'URL s'il existe
  // encore — un lien ancien peut désigner une fiche retirée — sinon le premier du dossier.
  const enfantChoisi =
    (enfantId && enfants.some((enfant) => enfant.id === enfantId) ? enfantId : null) ??
    enfants[0]?.id ??
    null;

  /**
   * Les trois blocs d'« Aujourd'hui », écrits une fois.
   *
   * Empilés dans le rail, répartis en volets sur téléphone : c'est la **disposition** qui
   * change, jamais le contenu. Les écrire deux fois les aurait fait diverger à la première
   * retouche, et un parent qui passe du téléphone à l'ordinateur n'y retrouverait plus les
   * mêmes mots.
   */
  const totalNouveautes = Object.values(parCategorie).reduce((somme, n) => somme + n, 0);

  const blocNouveautes = changements.data ? (
    <Nouveautes changements={changements.data} surChoix={surCategorie} />
  ) : null;

  // Deux cartes sur le rail, une seule sur téléphone : c'est le volet qui est la carte
  // (voir plus bas, `.volet-carte`), et les deux sujets s'y rangent l'un sous l'autre.
  // « Qui a l'enfant ? » n'a pas besoin de son propre titre — c'est déjà le nom de
  // l'onglet ouvert ; « Demandes d'accord » garde le sien en simple intitulé, puisque
  // rien d'autre ne le porte dans ce volet — et ne se rend que s'il y a des demandes
  // (voir `VoletDemandes`).
  const demandes = (
    <VoletDemandes
      caseFileId={dossierId}
      monUserId={monUserId}
      titre={m.demandesAccord}
      enCarte={rail}
    />
  );

  const blocGarde = rail ? (
    <>
      <Carte titre={m.quiALEnfant}>
        <QuiALEnfant caseFileId={dossierId} parents={parents} />
      </Carte>

      {demandes}
    </>
  ) : (
    <>
      <QuiALEnfant caseFileId={dossierId} parents={parents} />

      {demandes}
    </>
  );

  const modeSoloContenu = (
    <>
      <p style={{ marginTop: 0 }}>{m.modeSoloTexte}</p>

      {state.pendingInvitation && !renvoi ? (
        <>
          <p className="discret">{m.invitationEnvoyeeTexte}</p>
          <Bouton variante="lien" onClick={() => setRenvoi(true)}>
            {m.renvoyerOuCorriger}
          </Bouton>
        </>
      ) : (
        <>
          <p>{state.pendingInvitation ? m.nouvelleAdresseRemplace : m.peutInviterQuandVeut}</p>
          {inviter.isError ? (
            <Alerte titre={m.erreurInvitation}>{messageDErreur(inviter.error)}</Alerte>
          ) : null}
          <Champ
            label={m.emailAutreParent}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aide={m.aideInvitation}
          />
          <Bouton onClick={() => inviter.mutate()} disabled={!email || inviter.isPending}>
            {inviter.isPending ? m.envoiEnCours : m.envoyerInvitation}
          </Bouton>
        </>
      )}
    </>
  );

  // Sur téléphone, la carte est le volet lui-même : « Mode solo » y garde un simple
  // intitulé plutôt qu'une carte à lui, qui en ferait une dans une autre.
  /**
   * Ce que le dossier **est** : avec qui il est partagé, quels enfants il suit, sous
   * quelle formule on y écrit.
   *
   * Ces trois lignes tenaient un bandeau au-dessus de tout l'écran. Sur téléphone, elles
   * en sont parties les premières — une centaine de pixels, le tiers de ce qu'on voit en
   * arrivant, pour ce qu'on lit une fois. Sur grand écran, le bandeau coûtait moins cher
   * mais posait le même problème : il occupait la pleine largeur, au-dessus du rail comme
   * du contenu, pour trois informations dont aucune ne se consulte tous les jours.
   *
   * Elles sont donc à un seul endroit désormais, le même des deux côtés : « Le dossier »,
   * dans « Aujourd'hui ». Un volet sur téléphone, une carte sur grand écran — le contenu,
   * lui, ne se dédouble plus.
   */
  const blocIdentite = (
    <>
      <p className="dossier-mode" style={{ marginTop: 0 }}>
        {state.mode === 'solo'
          ? m.modeSolo
          : autreParent
            ? m.modePartageAvec(autreParent.displayName)
            : m.modePartage}
      </p>
      <p className="discret">{ages}</p>
      {/* Le chemin vers Solo ou Duo, à côté du régime du dossier et seulement pour un
          compte gratuit. Ailleurs, il fallait attendre une écriture refusée ou le passage
          du rappel : deux occasions qu'on ne choisit pas. */}
      <ChoisirFormule />
    </>
  );

  const blocDossier = (
    <>
      {/* Un intitulé et non une carte : sur les deux dispositions, ce bloc vit maintenant
          à l'intérieur du panneau « Le dossier » — une carte y serait une carte dans une
          carte. */}
      {state.mode === 'solo' ? (
        <>
          <h3>{m.modeSolo}</h3>
          {modeSoloContenu}
        </>
      ) : null}

      {/* La porte de sortie, en dernier et derrière un lien. Elle n'a pas de catégorie à
          elle et n'en méritait pas une : « Aujourd'hui » est le seul écran qui ne parle
          pas d'un module, donc le seul où elle ne s'intercale pas entre deux gestes du
          quotidien. Elle s'efface tant qu'une suppression est en cours — c'est alors le
          bandeau qui parle. */}
      {dossier.data.deletion ? null : (
        <SupprimerDossier caseFileId={dossierId} modeSolo={state.mode === 'solo'} />
      )}
    </>
  );

  return (
    <div className="dossier">
      <AlerteRecue caseFileId={dossierId} monUserId={monUserId} />

      <header className="dossier-entete">
        {/* Le dossier porte le nom que le parent lui donne : ses enfants. Le mot
            « Dossier » ne dit rien à personne, et l'appeler autrement ici que sur
            l'accueil donnerait l'impression de deux choses différentes. Ce nom s'écrit
            dans le bandeau (`Coquille.tsx`) ; ce `<h1>` reste invisible, pour le plan du
            document — c'est désormais tout ce que cet en-tête porte. */}
        <NomDuDossier
          label={dossier.data.label}
          enfants={enfants}
          schema={dossier.data.custodyKind}
        />
      </header>

      {/* Sous l'en-tête et au-dessus de la navigation : une suppression en cours concerne
          tout l'écran, pas l'onglet qu'on regarde. Reléguée dans « Aujourd'hui », elle
          serait invisible pour qui ouvre le dossier sur sa messagerie. */}
      {dossier.data.deletion ? (
        <BandeauSuppression caseFileId={dossierId} suppression={dossier.data.deletion} />
      ) : null}

      <Navigation categorieActive={categorie} nouveautes={parCategorie} surChoix={surCategorie} />

      {/* La catégorie nomme la **région**, elle n'ajoute pas de titre : un `<h2>` invisible
          « Messages » au-dessus d'une carte « Messages » ferait entendre deux fois la même
          chose au lecteur d'écran. `aria-label` donne le repère sans doubler le plan. */}
      <section
        className={
          categorie === 'messages' && !rail
            ? 'dossier-contenu dossier-contenu-messagerie'
            : categorie === 'journal' && !rail
              ? 'dossier-contenu dossier-contenu-journal'
              : 'dossier-contenu'
        }
        aria-label={messages.dossier.categories[categorie]}
      >
        {categorie === 'aujourdhui' ? (
          <>
            {/* En tête, et une seule fois dans la vie du dossier. Ailleurs qu'ici, la carte
                n'aurait été vue que par ceux qui font défiler ; plus bas dans la page, elle
                aurait manqué son seul et unique passage. Les jours suivants, « Aujourd'hui »
                reprend sa place — c'est l'écran qu'on ouvre pour savoir qui a l'enfant, et
                il ne porte plus rien à vendre.

                Hors des volets : c'est un passage unique, pas un volet où l'on revient. */}
            <PresentationFormules dossierId={dossierId} />

            {rail ? (
              <>
                {blocNouveautes}
                {blocGarde}
                {/* Le pendant du volet « Le dossier » du téléphone : même contenu, même
                    ordre, et une carte plutôt qu'un onglet parce que la colonne a la
                    place de tout montrer d'un regard. */}
                <Carte titre={m.ongletDossier}>
                  {blocIdentite}
                  {blocDossier}
                </Carte>
              </>
            ) : (
              <>
                {/* Le même dessin que les filtres de la messagerie et du journal, et que
                    les onglets de la fiche enfant : c'est le même geste — choisir ce
                    qu'on regarde dans un même écran.

                    `volets-aujourdhui` en plus de `filtres` : ces trois-là sont trois
                    destinations fixes, pas une liste de filtres qui peut s'enrichir et
                    déborder sur une seconde ligne sans dommage. Sur téléphone, la classe
                    supplémentaire les distingue pour ne compacter qu'eux — pas les
                    filtres de la messagerie ou du journal, ni les onglets de la fiche
                    enfant, qui vivent dans le même `.dossier-contenu`. */}
                <div
                  className="filtres volets-aujourdhui"
                  role="tablist"
                  aria-label={messages.dossier.categories.aujourdhui}
                >
                  <button
                    type="button"
                    role="tab"
                    id="onglet-garde"
                    aria-selected={volet === 'garde'}
                    aria-controls="volet-garde"
                    className={volet === 'garde' ? 'filtre-onglet actif' : 'filtre-onglet'}
                    onClick={() => choisirVolet('garde')}
                  >
                    {m.quiALEnfant}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="onglet-nouveautes"
                    aria-selected={volet === 'nouveautes'}
                    aria-controls="volet-nouveautes"
                    className={volet === 'nouveautes' ? 'filtre-onglet actif' : 'filtre-onglet'}
                    onClick={() => choisirVolet('nouveautes')}
                  >
                    {m.ongletNouveautes}
                    {/* Le compte sur l'onglet fermé : ranger le relevé derrière un volet
                        ne doit pas revenir à éteindre ce qu'il annonçait. */}
                    {totalNouveautes > 0 ? (
                      <span className="filtre-compte" aria-hidden="true">
                        {totalNouveautes > 99 ? '99+' : totalNouveautes}
                      </span>
                    ) : null}
                    {totalNouveautes > 0 ? (
                      <Invisible>
                        {messages.dossier.navigation.nouveaute(totalNouveautes)}
                      </Invisible>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="onglet-dossier"
                    aria-selected={volet === 'dossier'}
                    aria-controls="volet-dossier"
                    className={volet === 'dossier' ? 'filtre-onglet actif' : 'filtre-onglet'}
                    onClick={() => choisirVolet('dossier')}
                  >
                    {m.ongletDossier}
                  </button>
                </div>

                {/* Chaque volet est une carte.
                    Le contenu était posé à même le fond, sous la rangée d'onglets : rien
                    ne disait où finissait le volet, et le vide qui suit une réponse courte
                    se lisait comme la page, pas comme le bas d'un panneau. La carte donne
                    au volet un bord — c'est ce que la rangée d'onglets promet, un panneau
                    qu'on change — et rejoint ce que le rail montre déjà sur grand écran.

                    Sans titre : l'onglet sélectionné nomme déjà le volet, et le redire en
                    tête de carte ferait lire deux fois le même mot à 8 px d'écart. */}
                {volet === 'garde' ? (
                  <div
                    role="tabpanel"
                    id="volet-garde"
                    aria-labelledby="onglet-garde"
                    className="carte volet-carte"
                  >
                    {blocGarde}
                  </div>
                ) : null}

                {volet === 'nouveautes' ? (
                  <div
                    role="tabpanel"
                    id="volet-nouveautes"
                    aria-labelledby="onglet-nouveautes"
                    className="carte volet-carte"
                  >
                    {blocNouveautes}
                  </div>
                ) : null}

                {volet === 'dossier' ? (
                  <div
                    role="tabpanel"
                    id="volet-dossier"
                    aria-labelledby="onglet-dossier"
                    className="carte volet-carte"
                  >
                    {blocIdentite}
                    {blocDossier}
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : null}

        {categorie === 'calendrier' ? (
          rail ? (
            <>
              <Carte titre={m.calendrierDeGarde}>
                <Calendrier caseFileId={dossierId} parents={parents} />
              </Carte>

              <Carte titre={m.demanderEchange}>
                <DemandeEchange caseFileId={dossierId} parents={parents} />
              </Carte>

              <Carte titre={m.historiqueSchema}>
                <HistoriqueSchema caseFileId={dossierId} />
              </Carte>
            </>
          ) : (
            <>
              <div
                className="filtres volets-calendrier"
                role="tablist"
                aria-label={messages.dossier.categories.calendrier}
              >
                <button
                  type="button"
                  role="tab"
                  id="onglet-calendrier-grille"
                  aria-selected={voletCalendrier === 'grille'}
                  aria-controls="volet-calendrier-grille"
                  className={voletCalendrier === 'grille' ? 'filtre-onglet actif' : 'filtre-onglet'}
                  onClick={() => setVoletCalendrier('grille')}
                >
                  {m.calendrierDeGarde}
                </button>
                <button
                  type="button"
                  role="tab"
                  id="onglet-calendrier-echange"
                  aria-selected={voletCalendrier === 'echange'}
                  aria-controls="volet-calendrier-echange"
                  className={
                    voletCalendrier === 'echange' ? 'filtre-onglet actif' : 'filtre-onglet'
                  }
                  onClick={() => setVoletCalendrier('echange')}
                >
                  {m.demanderEchange}
                </button>
                <button
                  type="button"
                  role="tab"
                  id="onglet-calendrier-historique"
                  aria-selected={voletCalendrier === 'historique'}
                  aria-controls="volet-calendrier-historique"
                  className={
                    voletCalendrier === 'historique' ? 'filtre-onglet actif' : 'filtre-onglet'
                  }
                  onClick={() => setVoletCalendrier('historique')}
                >
                  {m.historiqueSchema}
                </button>
              </div>

              {/* Chaque volet est une carte, comme ceux d'« Aujourd'hui » : la rangée
                  d'onglets promet un panneau qu'on change, et sans bord rien ne disait où
                  il s'arrêtait. Sans titre en revanche — le libellé de l'onglet nomme déjà
                  le volet, et le redire à 8 px d'écart ferait lire deux fois le même mot
                  sur un écran qui tient déjà à l'étroit trois onglets et une grille. */}
              {voletCalendrier === 'grille' ? (
                <div
                  role="tabpanel"
                  id="volet-calendrier-grille"
                  aria-labelledby="onglet-calendrier-grille"
                  className="carte volet-carte"
                >
                  <Calendrier caseFileId={dossierId} parents={parents} />
                </div>
              ) : null}

              {voletCalendrier === 'echange' ? (
                <div
                  role="tabpanel"
                  id="volet-calendrier-echange"
                  aria-labelledby="onglet-calendrier-echange"
                  className="carte volet-carte"
                >
                  <DemandeEchange caseFileId={dossierId} parents={parents} />
                </div>
              ) : null}

              {voletCalendrier === 'historique' ? (
                <div
                  role="tabpanel"
                  id="volet-calendrier-historique"
                  aria-labelledby="onglet-calendrier-historique"
                  className="carte volet-carte"
                >
                  <HistoriqueSchema caseFileId={dossierId} />
                </div>
              ) : null}
            </>
          )
        ) : null}

        {categorie === 'messages' ? (
          rail ? (
            <Carte titre={messages.dossier.categories.messages}>
              <Messagerie
                caseFileId={dossierId}
                parents={parents}
                monUserId={monUserId}
                messagerieActive={state.messagingEnabled}
              />
            </Carte>
          ) : (
            /* Sans `<Carte>`, comme le Calendrier : le rail porte déjà le nom de la
               catégorie, et sur téléphone la conversation prend toute la hauteur qui
               reste (`.dossier-contenu-messagerie`) — une messagerie, ça se lit comme
               une appli de SMS, pas dans une carte qui la borne à une fenêtre de 340 px
               au milieu d'une page qui défile. */
            <Messagerie
              caseFileId={dossierId}
              parents={parents}
              monUserId={monUserId}
              messagerieActive={state.messagingEnabled}
            />
          )
        ) : null}

        {categorie === 'depenses' ? (
          rail ? (
            <Carte titre={m.depensesPartagees}>
              <Depenses caseFileId={dossierId} parents={parents} monUserId={monUserId} />
            </Carte>
          ) : (
            /* Pas de carte d'écran ici : les Dépenses portent leur propre rangée d'onglets,
               et ce sont les volets qui sont des cartes (`Depenses.tsx`, `.volet-carte`).
               Une carte d'écran par-dessus rendrait ce qu'on avait retiré — le solde dans
               la saisie dans l'écran, quatre bordures empilées avant le premier chiffre. */
            <Depenses caseFileId={dossierId} parents={parents} monUserId={monUserId} />
          )
        ) : null}

        {categorie === 'journal' ? (
          rail ? (
            <Carte titre={messages.dossier.categories.journal}>
              <Journal
                caseFileId={dossierId}
                parents={parents}
                monUserId={monUserId}
                modeSolo={state.mode === 'solo'}
              />
            </Carte>
          ) : (
            /* Sans `<Carte>`, comme la Messagerie et les Dépenses : le rail porte déjà le
               nom de la catégorie, et sur téléphone les filtres, le registre et la saisie
               n'ont plus à tenir dans une fenêtre bornée au milieu d'un écran qui défile
               déjà tout seul. */
            <Journal
              caseFileId={dossierId}
              parents={parents}
              monUserId={monUserId}
              modeSolo={state.mode === 'solo'}
            />
          )
        ) : null}

        {categorie === 'enfants' ? (
          <>
            {/* Le choix de l'enfant et l'ajout d'un enfant sur la même rangée, en tête :
                ce sont deux gestes sur la même liste, et l'ajout enfoui sous les fiches
                obligeait à traverser tout l'écran pour le trouver. Le formulaire s'ouvre
                sous la rangée, jamais dedans.
                Un enfant choisi qui ne serait plus dans le dossier — lien ancien, fiche
                retirée — retombe sur « Tous » plutôt que sur un écran vide. */}
            <div className="enfants-entete">
              <SelecteurEnfant
                enfants={enfants}
                enfantActif={enfantChoisi}
                surChoix={(id) => surEnfant?.(id)}
                maintenant={maintenant()}
              />
              <AjouterEnfant caseFileId={dossierId} />
            </div>
            {enfants
              .filter((enfant) => enfant.id === enfantChoisi)
              .map((enfant) => (
                <Carte key={enfant.id} titre={m.ficheDe(enfant.firstName)}>
                  {/* La correction du prénom et de la date vit désormais dans l'onglet
                      « Identité » de la fiche, avec l'avatar : c'est la même question. */}
                  <FicheEnfant caseFileId={dossierId} enfant={enfant} maintenant={maintenant()} />
                </Carte>
              ))}
          </>
        ) : null}
      </section>
    </div>
  );
}
